// ============================================================================
// project-client-match.ts (SPEC 2026-10-08 D2 to D5, lane A2)
// ----------------------------------------------------------------------------
// What the job on screen tells the client prompt, read from its Stage 1 data.
// Pure: no React, no network.
//
// The client of a strata job is the FIRM that sent the request (DEC-COUNCIL-018).
// The printed addressee "The Owners of Strata Plan" is never a client name, so
// owners wording is dropped wherever a firm name is read.
// ============================================================================

import { crmJobPlanDigits, isOwnersWording } from './client-record-view-model';
import { jobFirmHints, readJobRequesters } from '../../services/project-requesters';

export interface ProjectClientMatchInput {
    /** The requester email: the strongest signal (D2). */
    email: string;
    /** The firm name to search, in the D5 order (without the email-resolved firm, which only the server knows). */
    firmName: string;
    /** The firm the Quote Request names: formData.clientCompany unless it is owners wording. */
    pdfFirmName: string;
    /** "SP2068" when the job carries a plan, else ''. */
    plan: string;
    phone: string;
    /**
     * The job carries a strata plan or names the owners of a building. Only then
     * is the client the firm that sent the request, and the prompt says so (D5).
     * Any other job keeps the prompt it had before this change (I5).
     */
    strataJob: boolean;
}

const text = (value: unknown) => (typeof value === 'string' ? value.trim() : typeof value === 'number' ? String(value) : '');

/**
 * The prompt's inputs for one job.
 *
 * The firm name is requesterCompany, then clientCompany unless it is owners
 * wording. A job with no plan and no owners wording (a business without strata
 * plans) falls back to clientName as before, so its prompt searches exactly
 * what it searched before this change (I5). On a strata job clientName is the
 * strata manager's own name, never a firm, so the search starts empty.
 */
export function projectClientMatchInput(formData: Record<string, unknown> | null | undefined): ProjectClientMatchInput {
    const fd = formData || {};
    const requesterCompany = text(fd.requesterCompany);
    const clientCompany = text(fd.clientCompany);
    const ownersClient = isOwnersWording(clientCompany);
    const pdfFirmName = clientCompany && !ownersClient ? clientCompany : '';
    const planDigits = crmJobPlanDigits(fd);
    const strataJob = !!planDigits || ownersClient;
    const firmName = (requesterCompany && !isOwnersWording(requesterCompany) ? requesterCompany : '')
        || pdfFirmName
        || (strataJob ? '' : text(fd.clientName));
    return {
        email: text(fd.clientEmail),
        firmName,
        pdfFirmName,
        plan: planDigits ? `SP${planDigits}` : '',
        phone: text(fd.clientPhone),
        strataJob,
    };
}

/** True when the job gives the prompt anything to work from. */
export function hasClientMatchSignal(input: ProjectClientMatchInput): boolean {
    return !!(input.email || input.firmName || input.pdfFirmName);
}

/**
 * Decision E5 (2026-10-13): what else the RFQ names that can pick its firm on
 * the server. requesterNames are the people it names (a person, never a firm
 * name to search); firmNames, abns and formFingerprint come from the RFQ's
 * text (jobFirmHints), never from a file name.
 */
export interface ProjectClientMatchHints {
    requesterNames: string[];
    firmNames: string[];
    abns: string[];
    formFingerprint: string | null;
}

export function projectClientMatchHints(formData: Record<string, unknown> | null | undefined): ProjectClientMatchHints {
    const requesterNames = [...new Set(readJobRequesters(formData as Record<string, any> | null)
        .map(requester => requester.name.trim())
        .filter(name => name && !isOwnersWording(name)))].slice(0, 20);
    const hints = jobFirmHints(formData as Record<string, any> | null);
    return {
        requesterNames,
        firmNames: hints.firmNames.filter(name => !isOwnersWording(name)),
        abns: hints.abns,
        formFingerprint: hints.formFingerprint,
    };
}

/** The hints as resolve's body takes them: only the ones the RFQ gave. */
export function clientMatchHintsPayload(hints: ProjectClientMatchHints): Partial<ProjectClientMatchHints> {
    return {
        ...(hints.requesterNames.length ? { requesterNames: hints.requesterNames } : {}),
        ...(hints.firmNames.length ? { firmNames: hints.firmNames } : {}),
        ...(hints.abns.length ? { abns: hints.abns } : {}),
        ...(hints.formFingerprint ? { formFingerprint: hints.formFingerprint } : {}),
    };
}

/** E5: a plan, a named requester or an RFQ hint is enough to ask the server. */
export function hasClientMatchEvidence(input: ProjectClientMatchInput, hints: ProjectClientMatchHints): boolean {
    return hasClientMatchSignal(input) || !!input.plan || Object.keys(clientMatchHintsPayload(hints)).length > 0;
}
